import React, { useState, useRef, useEffect, useCallback } from "react";
import { useQuery, gql } from "@apollo/client";
import {
  Grid,
  Typography,
  TextField,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Paper,
  Chip,
  Fade,
  Grow,
  Button,
  Alert,
  Skeleton,
  Container,
  Divider,
  Snackbar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmptyStateIcon from "@mui/icons-material/NoMeals";
import { motion } from "framer-motion";
import { RecipeValidationProvider } from "../Context/RecipeValidationContext";
import HomePageRecipeCard from "../Components/Cards/HomePageRecipeCard";
import Preloader from "../Components/Preloader/Preloader";
import ErrorPage from "./ErrorPage";
import BreadCrumb from "../Components/BreadCrumb";

const MotionGrid = motion(Grid);

export default function RecipesList() {
  const { loading, error, data, refetch } = useQuery(GET_RECIPES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });
  const [recipes, setRecipes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedType, setSelectedType] = useState("All");
  const [preloader, setPreloader] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const categoryRef = useRef(null);
  const typeRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (data && data.allpublicRecipes) {
      setRecipes(data.allpublicRecipes);
    }
  }, [data]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPreloader(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (error) {
      setErrorMsg(error.message || "Error loading recipes. Please try again.");
    }
  }, [error]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (event, newCategory) => {
    setSelectedCategory(newCategory || "All");
  };

  const handleTypeChange = (event, newType) => {
    setSelectedType(newType || "All");
  };

  const scrollLeft = useCallback((ref) => {
    ref.current.scrollBy({ left: -200, behavior: "smooth" });
  }, []);

  const scrollRight = useCallback((ref) => {
    ref.current.scrollBy({ left: 200, behavior: "smooth" });
  }, []);

  const handleRefetch = async () => {
    try {
      setIsRefetching(true);
      setErrorMsg(null);
      await refetch();
      setSnackbarOpen(true);
    } catch (err) {
      setErrorMsg(err.message || "Failed to refresh recipes. Please try again.");
    } finally {
      setIsRefetching(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const filteredRecipes = recipes.filter((recipe) => {
    const categories = recipe.recipe_category
      .split(",")
      .map((cat) => cat.trim());
    const types = recipe.recipe_type.split(",").map((type) => type.trim());

    return (
      (selectedCategory === "All" || categories.includes(selectedCategory)) &&
      (selectedType === "All" || types.includes(selectedType)) &&
      recipe.recipe_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const getUniqueValues = (items, key) => {
    const allValues = items.flatMap((item) =>
      item[key].split(",").map((val) => val.trim())
    );
    return [...new Set(allValues)];
  };

  const uniqueCategories = [
    "All",
    ...getUniqueValues(recipes, "recipe_category"),
  ];

  const uniqueTypes = ["All", ...getUniqueValues(recipes, "recipe_type")];

  if (error && !data) {
    return (
      <ErrorPage 
        message={errorMsg || "Error loading recipes."} 
        retryAction={handleRefetch}
      />
    );
  }

  const renderLoadingSkeletons = () => (
    <Grid container spacing={3}>
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <Grid item xs={12} sm={6} md={4} key={item}>
          <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
            <Skeleton variant="rectangular" height={180} sx={{ borderRadius: 1, mb: 2 }} />
            <Skeleton variant="text" height={30} width="80%" />
            <Skeleton variant="text" height={20} width="60%" />
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="rectangular" width={70} height={30} />
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <>
      <BreadCrumb
        links={[{ label: "Dashboard", href: "/dashboard" }]}
        currentPage={"Recipes"}
      />
    <Container maxWidth="xl">

      {loading || preloader ? (
        <Fade in={loading || preloader}>
          <Box>
            <Preloader />
          </Box>
        </Fade>
      ) : (
        <Grow in={!loading && !preloader} timeout={500}>
          <Paper
            elevation={3}
            sx={{
              padding: { xs: 2, sm: 3 },
              marginBottom: 3,
              minHeight: "calc(100vh - 120px)",
              borderRadius: 2,
            }}
          >
            {/* Header */}
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RestaurantMenuIcon fontSize="large" color="primary" />
                <Typography variant="h5" component="h1" fontWeight="bold">
                  Recipe Library
                </Typography>
              </Box>
              
              <Button 
                startIcon={<RefreshIcon />} 
                onClick={handleRefetch}
                disabled={isRefetching}
                variant="outlined"
                color="primary"
              >
                {isRefetching ? "Refreshing..." : "Refresh"}
              </Button>
            </Box>
            
            {errorMsg && (
              <Alert 
                severity="error" 
                sx={{ mb: 3 }}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={handleRefetch}
                  >
                    RETRY
                  </Button>
                }
              >
                {errorMsg}
              </Alert>
            )}

            {/* Search and Filters */}
            <Box sx={{ mb: 4 }}>
              <TextField
                label="Search Recipes"
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: <SearchIcon position="start" sx={{ mr: 1 }} />,
                }}
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', flexDirection: isMedium ? 'column' : 'row', gap: 2 }}>
                <Box 
                  sx={{ 
                    flex: 1,
                    p: 2, 
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                    Filter by Category:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => scrollLeft(categoryRef)} size={isMobile ? "small" : "medium"}>
                      <ArrowBackIosIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                    
                    <Box
                      ref={categoryRef}
                      sx={{ overflow: "hidden", whiteSpace: "nowrap", flexGrow: 1, px: 1 }}
                    >
                      <ToggleButtonGroup
                        value={selectedCategory}
                        exclusive
                        onChange={handleCategoryChange}
                        aria-label="Recipe Category"
                        sx={{ gap: 0.5 }}
                      >
                        {uniqueCategories.map((category) => (
                          <Chip
                            key={category}
                            label={category}
                            clickable
                            onClick={(e) => handleCategoryChange(e, category)}
                            color={selectedCategory === category ? "primary" : "default"}
                            variant={selectedCategory === category ? "filled" : "outlined"}
                            sx={{ mx: 0.25 }}
                          />
                        ))}
                      </ToggleButtonGroup>
                    </Box>
                    
                    <IconButton onClick={() => scrollRight(categoryRef)} size={isMobile ? "small" : "medium"}>
                      <ArrowForwardIosIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Box>
                </Box>

                <Box 
                  sx={{ 
                    flex: 1,
                    p: 2, 
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                    Filter by Type:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => scrollLeft(typeRef)} size={isMobile ? "small" : "medium"}>
                      <ArrowBackIosIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                    
                    <Box
                      ref={typeRef}
                      sx={{ overflow: "hidden", whiteSpace: "nowrap", flexGrow: 1, px: 1 }}
                    >
                      <ToggleButtonGroup
                        value={selectedType}
                        exclusive
                        onChange={handleTypeChange}
                        aria-label="Recipe Type"
                        sx={{ gap: 0.5 }}
                      >
                        {uniqueTypes.map((type) => (
                          <Chip
                            key={type}
                            label={type}
                            clickable
                            onClick={(e) => handleTypeChange(e, type)}
                            color={selectedType === type ? "primary" : "default"}
                            variant={selectedType === type ? "filled" : "outlined"}
                            sx={{ mx: 0.25 }}
                          />
                        ))}
                      </ToggleButtonGroup>
                    </Box>
                    
                    <IconButton onClick={() => scrollRight(typeRef)} size={isMobile ? "small" : "medium"}>
                      <ArrowForwardIosIcon fontSize={isMobile ? "small" : "medium"} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Recipe Results */}
            {isRefetching ? (
              renderLoadingSkeletons()
            ) : (
              <>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                  Showing {filteredRecipes?.length} {filteredRecipes?.length === 1 ? 'recipe' : 'recipes'}
                </Typography>
                
                <Grid container spacing={3}>
                  {filteredRecipes?.length > 0 ? (
                    filteredRecipes?.map((recipe, index) => (
                      <MotionGrid 
                        item 
                        xs={12} 
                        sm={6} 
                        md={4} 
                        key={recipe.id}
                        component={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <RecipeValidationProvider recipe_id={recipe.id}>
                          <HomePageRecipeCard
                            recipe={recipe}
                            navigationPath="recipe"
                            refresh={refetch}
                          />
                        </RecipeValidationProvider>
                      </MotionGrid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          justifyContent: 'center',
                          py: 5,
                          textAlign: 'center'
                        }}
                      >
                        <EmptyStateIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No recipes found
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500 }}>
                          Try adjusting your search or filters to find what you're looking for
                        </Typography>
                        <Button 
                          variant="contained" 
                          onClick={() => {
                            setSearchTerm('');
                            setSelectedCategory('All');
                            setSelectedType('All');
                          }}
                        >
                          Clear filters
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </>
            )}

            <Snackbar
              open={snackbarOpen}
              autoHideDuration={3000}
              onClose={handleCloseSnackbar}
              message="Recipes refreshed successfully"
            />
          </Paper>
        </Grow>
       )} 
    </Container>
    </>
  );
}

const GET_RECIPES = gql`
  query GetRecipes {
    allpublicRecipes {
      id
      recipe_name
      viewsCount
      sharedCount
      likedCount
      cookedCount
      downloadedCount
      created_by
      duration
      recipe_category
      recipe_type
      recipe_status
      recipe_visiblity
      serving
      prep_time
      cook_time
      isLiked
      averageRating
      ratingCount
      recipe_image
    }
  }
`;
